import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServices } from "@/services/ServiceProvider";
import { useViewer } from "@/hooks/useOrganizations";
import { getChainConfig } from "@/config/contracts";
import type { CreateOrgPollInput } from "@/services/interfaces/IOrgHubService";
import type { OrgFile } from "@/lib/orgHub";

/**
 * Org hub keys sit under the same "orgs" prefix as useOrganizations (and end
 * with the viewer), so leaving an org or switching accounts invalidates them
 * with everything else org-shaped.
 */
export const orgHubKeys = {
  quests: (orgId: string | undefined, chainId: number, viewer: string | null) =>
    ["orgs", "hub", "quests", orgId, chainId, viewer] as const,
  messages: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "messages", orgId, viewer] as const,
  // Under the messages key, so sending a message (which refreshes messages) refreshes this too.
  sentByMe: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "messages", orgId, viewer, "mine"] as const,
  readAt: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "read-at", orgId, viewer] as const,
  polls: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "polls", orgId, viewer] as const,
  files: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "files", orgId, viewer] as const,
  // Under the files key, so an upload (which refreshes files) refreshes this too.
  uploadedByMe: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "files", orgId, viewer, "mine"] as const,
  // Quests are posted from the Assistant, outside the hub: this refetches on mount instead.
  postedByMe: (orgId: string | undefined, viewer: string | null) => ["orgs", "hub", "posted-quest", orgId, viewer] as const,
  // Outside the files prefix: a rename refreshes the list without re-minting
  // (and so re-downloading) every thumbnail. New or deleted paths change the key.
  fileThumbs: (orgId: string | undefined, viewer: string | null, paths: readonly string[]) =>
    ["orgs", "hub", "file-thumbs", orgId, viewer, paths] as const,
};

/** Chat refresh while the thread is open; Realtime is off under Privy auth. */
export const ORG_CHAT_POLL_MS = 5_000;
/** Unread badge refresh when the thread is not open. */
export const ORG_CHAT_BADGE_POLL_MS = 60_000;

export function useOrgQuests(orgId: string | undefined) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  const chainId = getChainConfig().chainId;
  return useQuery({
    queryKey: orgHubKeys.quests(orgId, chainId, viewer),
    queryFn: () => orgHub.listQuests(orgId as string, chainId),
    enabled: !!orgId && !!viewer,
  });
}

export function useOrgMessages(orgId: string | undefined, live: boolean) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.messages(orgId, viewer),
    queryFn: () => orgHub.listMessages(orgId as string),
    enabled: !!orgId && !!viewer,
    refetchInterval: live ? ORG_CHAT_POLL_MS : ORG_CHAT_BADGE_POLL_MS,
  });
}

export function useOrgChatReadAt(orgId: string | undefined) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.readAt(orgId, viewer),
    queryFn: () => orgHub.getChatReadAt(orgId as string),
    enabled: !!orgId && !!viewer,
  });
}

export function useOrgHasSentMessage(orgId: string | undefined, enabled = true) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.sentByMe(orgId, viewer),
    queryFn: () => orgHub.hasSentMessage(orgId as string),
    enabled: enabled && !!orgId && !!viewer,
  });
}

export function useOrgHasUploadedFile(orgId: string | undefined, enabled = true) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.uploadedByMe(orgId, viewer),
    queryFn: () => orgHub.hasUploadedFile(orgId as string),
    enabled: enabled && !!orgId && !!viewer,
  });
}

export function useOrgHasPostedQuest(orgId: string | undefined, enabled = true) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.postedByMe(orgId, viewer),
    queryFn: () => orgHub.hasPostedQuest(orgId as string),
    enabled: enabled && !!orgId && !!viewer,
  });
}

export function useOrgPolls(orgId: string | undefined, enabled = true) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.polls(orgId, viewer),
    queryFn: () => orgHub.listPolls(orgId as string),
    enabled: enabled && !!orgId && !!viewer,
  });
}

export function useOrgFiles(orgId: string | undefined) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.files(orgId, viewer),
    queryFn: () => orgHub.listFiles(orgId as string),
    enabled: !!orgId && !!viewer,
  });
}

/** Mint a short-lived link to open (or, with a name, download) one org file. */
export function useOrgFileUrl() {
  const { orgHub } = useServices();
  return (path: string, download?: string) => orgHub.fileUrl(path, download);
}

/** Signed links for gallery thumbnails; refreshed before the 10-minute links expire. */
export function useOrgFileThumbs(orgId: string | undefined, paths: readonly string[]) {
  const { orgHub } = useServices();
  const viewer = useViewer();
  return useQuery({
    queryKey: orgHubKeys.fileThumbs(orgId, viewer, [...paths].sort()),
    queryFn: () => orgHub.fileUrls(paths),
    enabled: !!orgId && !!viewer && paths.length > 0,
    staleTime: 8 * 60_000,
    refetchInterval: 8 * 60_000,
  });
}

/** Every hub write, each refreshing only the list it changed. */
export function useOrgHubMutations(orgId: string) {
  const { orgHub } = useServices();
  const queryClient = useQueryClient();
  const viewer = useViewer();
  const refresh = (key: readonly unknown[]) => () => { void queryClient.invalidateQueries({ queryKey: key }); };
  const messages = refresh(orgHubKeys.messages(orgId, viewer));
  const polls = refresh(orgHubKeys.polls(orgId, viewer));
  const files = refresh(orgHubKeys.files(orgId, viewer));

  return {
    sendMessage: useMutation({ mutationFn: (body: string) => orgHub.sendMessage(orgId, body), onSuccess: messages }),
    deleteMessage: useMutation({ mutationFn: (id: string) => orgHub.deleteMessage(id), onSuccess: messages }),
    markChatRead: useMutation({
      mutationFn: (through: string) => orgHub.markChatRead(orgId, through),
      onSuccess: refresh(orgHubKeys.readAt(orgId, viewer)),
    }),
    createPoll: useMutation({ mutationFn: (input: CreateOrgPollInput) => orgHub.createPoll(orgId, input), onSuccess: polls }),
    vote: useMutation({ mutationFn: (v: { pollId: string; option: number }) => orgHub.vote(v.pollId, v.option), onSuccess: polls }),
    closePoll: useMutation({ mutationFn: (pollId: string) => orgHub.closePoll(pollId), onSuccess: polls }),
    uploadFile: useMutation({ mutationFn: (file: File) => orgHub.uploadFile(orgId, file), onSuccess: files }),
    renameFile: useMutation({ mutationFn: (v: { id: string; name: string }) => orgHub.renameFile(v.id, v.name), onSuccess: files }),
    deleteFile: useMutation({ mutationFn: (file: Pick<OrgFile, "id" | "path">) => orgHub.deleteFile(file), onSuccess: files }),
  };
}
